import {
	Button,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Calendar,
} from '@/components/ui';
import { ButtonFilterOptions } from '@/components/custom';
import { EventTypeEnum, EventStatusEnum } from '@/enums';
import { CalendarIcon, CircleX, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { ISelectOption } from '@/interfaces';
import useEvents from '../hooks/useEvents';
import routes from '@/lib/routes';

const TYPE_OPTIONS: ISelectOption[] = [
	{ value: EventTypeEnum.BLIND_DATING, title: 'Blind Dating' },
];

const STATUS_OPTIONS: ISelectOption[] = [
	{ value: EventStatusEnum.SCHEDULED, title: 'Programmé' },
	{ value: EventStatusEnum.LIVE, title: 'En cours' },
	{ value: EventStatusEnum.ENDED, title: 'Terminé' },
];

type TProps = {
	isLoading: boolean;
	activeFilters: ReturnType<typeof useEvents>['activeFilters'];
	handleFilterChange: (key: string, values: string[]) => void;
	handlePeriodChange: (from?: string, to?: string) => void;
};

const HeaderPage = ({
	isLoading,
	activeFilters,
	handleFilterChange,
	handlePeriodChange,
}: TProps) => {
	const periodFrom = activeFilters.startsAtFrom ? new Date(activeFilters.startsAtFrom) : undefined;
	const periodTo = activeFilters.startsAtTo ? new Date(activeFilters.startsAtTo) : undefined;
	const hasPeriod = !!(periodFrom || periodTo);

	return (
		<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
			{/* Filters */}
			<div className="flex items-center gap-2 overflow-x-auto pb-0.5 md:flex-wrap md:overflow-visible scrollbar-hide">
				<ButtonFilterOptions
					disabled={isLoading}
					options={TYPE_OPTIONS}
					value={activeFilters.type}
					text="Type"
					onChange={(opts) => handleFilterChange('type', opts.map(o => o.value as string))}
				/>
				<ButtonFilterOptions
					disabled={isLoading}
					options={STATUS_OPTIONS}
					value={activeFilters.status}
					text="Statut"
					onChange={(opts) => handleFilterChange('status', opts.map(o => o.value as string))}
				/>

				{/* Date range picker */}
				<div className="flex items-center my-auto">
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant={hasPeriod ? 'secondary' : 'ghost'}
								size="sm"
								disabled={isLoading}
								className={cn(
									'border border-dashed text-xs gap-1.5',
									hasPeriod && 'border-r-0 rounded-r-none'
								)}
							>
								<CalendarIcon className="h-3.5 w-3.5" />
								{periodFrom && periodTo
									? `${format(periodFrom, 'dd MMM', { locale: fr })} → ${format(periodTo, 'dd MMM yyyy', { locale: fr })}`
									: periodFrom
										? `Depuis ${format(periodFrom, 'dd MMM yyyy', { locale: fr })}`
										: 'Période'}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="start">
							<Calendar
								mode="range"
								selected={{ from: periodFrom, to: periodTo }}
								onSelect={(range) => {
									handlePeriodChange(
										range?.from?.toISOString(),
										range?.to?.toISOString(),
									);
								}}
								numberOfMonths={2}
								autoFocus
							/>
						</PopoverContent>
					</Popover>
					{hasPeriod && (
						<Button
							className="border border-dashed border-l-0 rounded-l-none text-xs"
							variant="secondary"
							size="sm"
							title="Supprimer le filtre"
							disabled={isLoading}
							onClick={() => handlePeriodChange(undefined, undefined)}
						>
							<CircleX size={16} />
						</Button>
					)}
				</div>
			</div>

			{/* Create button */}
			<a href={routes.nav.events.create.getHref()} target="_blank" rel="noopener noreferrer">
				<Button size="sm" className="shrink-0">
					<Plus className="mr-2 h-4 w-4" />
					Créer un événement
				</Button>
			</a>
		</div>
	);
};

export default HeaderPage;
